import { useTranslation } from "react-i18next"

import type { IncidentFields } from "../schemas"

import { Loader2 } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { cn } from "@/lib/utils"

import { EscalationToggle } from "../escalation-toggle"

type IncidentStepProps = {
  values: IncidentFields
  onChange: (values: IncidentFields) => void
  onBack: () => void
  onSubmit: () => void
  submitting: boolean
  evidenceSlot?: React.ReactNode
  screenshotSlot?: React.ReactNode
  /** User's consent to escalate. Incidents are always eligible; default on. */
  escalationRequested: boolean
  onEscalationRequestedChange: (requested: boolean) => void
}

export function IncidentStep({
  values,
  onChange,
  onBack,
  onSubmit,
  submitting,
  evidenceSlot,
  screenshotSlot,
  escalationRequested,
  onEscalationRequestedChange,
}: IncidentStepProps) {
  const { t } = useTranslation()

  const update = <K extends keyof IncidentFields>(key: K, value: IncidentFields[K]) => {
    onChange({ ...values, [key]: value })
  }

  const canContinue = values.summary.trim().length > 0 && values.impact.trim().length > 0

  return (
    <div className="space-y-4">
      <EscalationToggle
        requested={escalationRequested}
        checkboxLabel={t(
          "feedback.incident.escalationCheckbox",
          "Notify support now — expect a reply within 1 business day"
        )}
        description={t(
          "feedback.incident.note",
          "Incidents are routed to support by default. Uncheck below if you don't need an immediate reply."
        )}
        onToggle={onEscalationRequestedChange}
      />

      <div className="space-y-2">
        <label className="text-sm font-medium" htmlFor="incident-summary">
          {t("feedback.incident.summaryLabel", "What's happening?")}
          <span className="text-destructive ml-0.5">*</span>
        </label>
        <Textarea
          className="focus-visible:border-primary focus-visible:ring-0"
          id="incident-summary"
          rows={3}
          value={values.summary}
          placeholder={t(
            "feedback.incident.summaryPlaceholder",
            "e.g. Chat returns errors for all users in the team since 10:00."
          )}
          onChange={(e) => update("summary", e.target.value)}
        />
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium" htmlFor="incident-impact">
          {t("feedback.incident.impactLabel", "Who is affected and how?")}
          <span className="text-destructive ml-0.5">*</span>
        </label>
        <Textarea
          className="focus-visible:border-primary focus-visible:ring-0"
          id="incident-impact"
          rows={3}
          value={values.impact}
          placeholder={t(
            "feedback.incident.impactPlaceholder",
            "Number of people, what they're blocked from doing, business consequence."
          )}
          onChange={(e) => update("impact", e.target.value)}
        />
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium" htmlFor="incident-timeframe">
          {t("feedback.incident.timeframeLabel", "When did it start?")}
        </label>
        <Input
          className="focus-visible:border-primary focus-visible:ring-0"
          id="incident-timeframe"
          value={values.timeframe ?? ""}
          placeholder={t(
            "feedback.incident.timeframePlaceholder",
            "e.g. ~10:15 today, started after the deploy"
          )}
          onChange={(e) => update("timeframe", e.target.value)}
        />
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium" htmlFor="incident-error-text">
          {t("feedback.incident.errorTextLabel", "Error text (if any)")}
        </label>
        <Textarea
          className={cn("focus-visible:border-primary focus-visible:ring-0", "font-mono text-xs")}
          id="incident-error-text"
          rows={2}
          value={values.errorText ?? ""}
          placeholder={t(
            "feedback.incident.errorTextPlaceholder",
            "Paste the exact error or status message"
          )}
          onChange={(e) => update("errorText", e.target.value)}
        />
      </div>

      {evidenceSlot}
      {screenshotSlot}

      <div className="flex justify-between gap-2 pt-2">
        <Button disabled={submitting} variant="outline" onClick={onBack}>
          {t("common.back", "Back")}
        </Button>
        <Button disabled={submitting || !canContinue} onClick={onSubmit}>
          {submitting ? (
            <>
              <Loader2 className="size-4 animate-spin" />
              {t("common.sending", "Sending...")}
            </>
          ) : (
            t("feedback.actions.review", "Review")
          )}
        </Button>
      </div>
    </div>
  )
}
